/**
 * Map an agent `lastResponse` cart mutation onto the global toaster.
 * Does not navigate and does not call the Cart API.
 *
 * Combined turns (add_to_cart + get_product_details / search_products) keep
 * the cart line on `cart_mutation` because `action.product` is the navigation
 * target (fifth pant, search hits, …), not the mutated cart line.
 */

import { showCartToast } from "./toastManager";

function intentList(lastResponse) {
  const raw = lastResponse?.intent;
  if (Array.isArray(raw)) return raw.map(String);
  if (raw != null && raw !== "") return [String(raw)];
  return [];
}

function asProducts(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v) => v && typeof v === "object");
  if (typeof value === "object") return [value];
  return [];
}

function uniqueProducts(list) {
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const key = String(
      item.id ?? item.product_id ?? item.article_id ?? item.title ?? item.name ?? ""
    );
    const size = item.size ?? item.attributes?.size?.[0] ?? "";
    const dedupe = `${key}::${size}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);
    out.push(item);
  }
  return out;
}

function pickCartMutation(lastResponse) {
  const m = lastResponse?.cart_mutation;
  if (m != null && typeof m === "object" && !Array.isArray(m)) return m;
  return null;
}

function productFromCartById(action, lastResponse) {
  const id = action?.article_id ?? action?.product_id ?? action?.id;
  if (id == null) return [];
  const cart = lastResponse?.cart || action?.cart || pickCartMutation(lastResponse)?.cart;
  const items = Array.isArray(cart?.items)
    ? cart.items
    : Array.isArray(cart)
      ? cart
      : [];
  const found = items.find(
    (it) =>
      it &&
      String(it.product_id ?? it.article_id ?? it.id ?? "") === String(id)
  );
  return found ? [found] : [];
}

function productsFromAction(action, lastResponse) {
  const fromAction = [
    ...asProducts(action?.cart_item),
    ...asProducts(action?.item),
  ];
  if (action?.type === "ADD_TO_CART" || action?.type === "REMOVE_FROM_CART") {
    fromAction.push(...asProducts(action?.product));
  }
  if (fromAction.length) return uniqueProducts(fromAction);
  return productFromCartById(action, lastResponse);
}

function productsForCartToast(action, lastResponse, mutation) {
  if (mutation?.product != null) {
    return uniqueProducts(asProducts(mutation.product));
  }
  return productsFromAction(action, lastResponse);
}

function isPendingSize(action, mutation) {
  return (
    mutation?.status === "PENDING_SIZE_CONFIRMATION" ||
    action?.status === "PENDING_SIZE_CONFIRMATION"
  );
}

function readTurn(lastResponse) {
  const mutation = pickCartMutation(lastResponse);
  const action =
    lastResponse.action != null &&
    typeof lastResponse.action === "object" &&
    !Array.isArray(lastResponse.action)
      ? lastResponse.action
      : {};
  const intent = intentList(lastResponse);
  const isAdd =
    mutation?.type === "ADD_TO_CART" ||
    intent.includes("add_to_cart") ||
    action.type === "ADD_TO_CART";
  return { intent, mutation, action, isAdd };
}

/**
 * The products a turn successfully put in the cart, or `null` when the turn
 * was not a successful add (another mutation, a failure, or a size question).
 * An empty array means "added, but the response named no product".
 *
 * @param {object | null | undefined} lastResponse
 * @returns {object[] | null}
 */
export function agentCartAddedProducts(lastResponse) {
  if (!lastResponse || typeof lastResponse !== "object") return null;
  const { mutation, action, isAdd } = readTurn(lastResponse);
  if (!isAdd) return null;
  if (isPendingSize(action, mutation)) return null;
  if (mutation?.status && mutation.status !== "SUCCESS") return null;
  return productsForCartToast(action, lastResponse, mutation);
}

/**
 * Show a product-card toast for silent cart intents. Safe to call for any
 * lastResponse — no-ops when the turn is not a successful cart mutation.
 *
 * @param {object | null | undefined} lastResponse
 * @returns {string | null} toast id
 */
export function showAgentCartToast(lastResponse) {
  if (!lastResponse || typeof lastResponse !== "object") return null;

  const { intent, mutation, action, isAdd } = readTurn(lastResponse);

  if (isAdd) {
    const added = agentCartAddedProducts(lastResponse);
    if (added === null) return null;
    return showCartToast("add", added);
  }

  const isClear =
    mutation?.type === "CLEAR_CART" ||
    intent.includes("clear_cart") ||
    action.type === "CLEAR_CART";
  if (isClear) {
    return showCartToast("clear");
  }

  const isRemove =
    mutation?.type === "REMOVE_FROM_CART" ||
    intent.includes("remove_from_cart");
  if (isRemove) {
    if (isPendingSize(action, mutation)) return null;
    if (action.status === "NOT_IN_CART" || mutation?.status === "NOT_IN_CART") {
      return null;
    }
    const op =
      mutation?.remove_action === "DECREMENT" || action.remove_action === "DECREMENT"
        ? "update"
        : "remove";
    return showCartToast(op, productsForCartToast(action, lastResponse, mutation));
  }

  const isUpdate =
    mutation?.type === "UPDATE_CART_QUANTITY" ||
    intent.includes("update_cart_quantity");
  if (isUpdate) {
    if (mutation?.status && mutation.status !== "SUCCESS") return null;
    if (action.status && action.status !== "SUCCESS" && action.type === "UPDATE_CART_QUANTITY") {
      return null;
    }
    return showCartToast("update", productsForCartToast(action, lastResponse, mutation));
  }

  return null;
}
